import {useState, useMemo, useCallback} from 'react';
import {FormattedMessage} from 'react-intl';
import {groupBy} from 'lodash';
import {useQuery} from '@apollo/client';
import {isFolder, DocumentType, FolderTypename, type FoldersAndBookmarks, type BookmarkFolder, type Bookmark} from 'types';
import {GET_BOOKMARKS_QUERY, GET_BOOKMARK_FOLDERS_QUERY, type GetBookmarksOutput, type GetBookmarkFoldersOutput} from 'graphql/admin/bookmarks';
import {useSelectedLinkContext} from 'contexts/SelectedLinkContext';
import AdminBookmarksActions from './AdminBookmarksActions';
import AdminBookmarksCardList from './AdminBookmarksCardList';
import {Grid} from '@mui/material';
import AdminPageTemplate from 'components/ui/templates/AdminPageTemplate';

const AdminBookmarks = () : JSX.Element => {
  const { selectedLink: { mainUser, channelKey: channel } } = useSelectedLinkContext();
  const { firstName, lastName } = mainUser;

  const { data: foldersData, loading: foldersLoading } = useQuery<GetBookmarkFoldersOutput>(GET_BOOKMARK_FOLDERS_QUERY, {
    variables: {
      adminFoldersByFolderTypeInput: {
        channel,
        folderType: DocumentType.BOOKMARK
      }
    }
  });

  const { data: bookmarksData, loading: bookmarksLoading } = useQuery<GetBookmarksOutput>(GET_BOOKMARKS_QUERY, {
    variables: {
      getBookmarksInput: {
        channel,
        unlockedOnly: true
      }
    }
  });

  const [selectedFolderKey, setSelectedFolderKey] = useState<string | null>(null);
  
  const foldersAndUnfoldedBookmarks : FoldersAndBookmarks = useMemo(function assignBookmarksToFoldersThenConcatFoldersAndUnfoldedBookmarks() {
    const folders = foldersData?.adminFoldersByFolderType?.folders || [];
    const bookmarks = bookmarksData?.getBookmarks?.bookmarks || [];
    
    const {'undefined': unfoldedBookmarks = [], ...bookmarksGroupedByFolderKey} = groupBy(bookmarks, 'folders.[0].key');

    const foldersWithBookmarks : BookmarkFolder[] = folders.map(adminFolder => ({
      ...adminFolder,
      __typename: FolderTypename,
      bookmarks: bookmarksGroupedByFolderKey[adminFolder.key] || [],
    }));

    return [...foldersWithBookmarks, ...unfoldedBookmarks];
  }, [bookmarksData?.getBookmarks?.bookmarks, foldersData?.adminFoldersByFolderType?.folders]);

  const selectedFolder = useMemo(function getSelectedFolder() {

    if (!selectedFolderKey) return null;

    const isSelectedFolder = (folderOrBookmark : BookmarkFolder | Bookmark) : folderOrBookmark is BookmarkFolder => isFolder(folderOrBookmark) && selectedFolderKey === folderOrBookmark?.key;

    const selectedFolder = foldersAndUnfoldedBookmarks.find(isSelectedFolder);

    return selectedFolder;
  }, [foldersAndUnfoldedBookmarks, selectedFolderKey]);

  const createFolderClickHandler = useCallback((folder : BookmarkFolder) => () => setSelectedFolderKey(folder?.key), []);

  const handleBack = useCallback(() => setSelectedFolderKey(null), []);

  return (
    <AdminPageTemplate title={<FormattedMessage id="admin.bookmarks.title" values={{ firstName, lastName }} />}>
      <div className='flex flex-col gap-6w'>
        <AdminBookmarksActions selectedFolderKey={selectedFolderKey} />
        <Grid container rowSpacing={2} columnSpacing={4} className='bg-white rounded-md pr-4w pb-2w'>
          <AdminBookmarksCardList
            loading={foldersLoading || bookmarksLoading}
            foldersAndUnfoldedBookmarks={foldersAndUnfoldedBookmarks}
            selectedFolder={selectedFolder}
            handleBack={handleBack}
            createFolderClickHandler={createFolderClickHandler}
          />
        </Grid>
      </div>
    </AdminPageTemplate>
  );
}

export default AdminBookmarks;
